import {
    CssBaseline,
    StyledEngineProvider,
    ThemeProvider,
} from "@mui/material";
import MfaSettings from "components/Admin/UserManager/MfaSettings";
import { AcxMessageBox as MessageBox } from "components/UI/AcxMessageBox";
import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
    ApplicationPaths,
    QueryParameterNames,
} from "components/Auth/api-authorization/ApiAuthorizationConstants";
import { AuthStore } from "stores/AuthStore";
import LocalStorage from "stores/LocalStorage";
import AppTheme from "Theme/AppTheme";
import { useStore } from "utils/useStore";
import AuthPage from "./AuthPage";

function MfaRequired() {
    const authStore = useStore(AuthStore);
    const localStore = useStore(LocalStorage);
    const [searchParams] = useSearchParams();
    const [isTargetReady, setIsTargetReady] = useState(false);
    const [loadErrorMessage, setLoadErrorMessage] = useState("");

    const targetOrgId = searchParams.get("targetOrgId");
    const returnUrl = searchParams.get("returnUrl") || "/app";

    useEffect(() => {
        let isMounted = true;

        const prepareMfaContext = async () => {
            setIsTargetReady(false);
            setLoadErrorMessage("");

            try {
                const accessToken = await authStore.getAccessToken();

                if (!isMounted) {
                    return;
                }

                if (!accessToken) {
                    const redirectUrl = `${ApplicationPaths.Login}?${
                        QueryParameterNames.ReturnUrl
                    }=${encodeURIComponent(window.location.href)}`;
                    window.location.assign(redirectUrl);
                    return;
                }

                if (targetOrgId) {
                    authStore.setActiveOrgId(targetOrgId);
                    sessionStorage.setItem(
                        "pendingMfaTargetOrgId",
                        targetOrgId,
                    );
                }

                setIsTargetReady(true);
            } catch {
                if (isMounted) {
                    setLoadErrorMessage(
                        "Unable to load MFA setup. Please sign in again.",
                    );
                }
            }
        };

        void prepareMfaContext();

        return () => {
            isMounted = false;
        };
    }, [authStore, targetOrgId]);

    const handleContinue = async () => {
        await authStore.silentRenewAccess();

        if (targetOrgId) {
            authStore.setActiveOrgId(targetOrgId);
            sessionStorage.setItem("pendingMfaTargetOrgId", targetOrgId);
            void localStore
                .getLocalStore()
                .setItem("selectedOrgID", targetOrgId);
        }

        const nextUrl = returnUrl.includes("/mfa-required")
            ? "/app"
            : returnUrl;

        window.location.assign(nextUrl);
    };

    const handleCancel = async () => {
        await authStore.handleUserLogout();
    };

    return (
        <StyledEngineProvider injectFirst>
            <ThemeProvider theme={AppTheme}>
                <CssBaseline />
                <AuthPage
                    isLoading={!isTargetReady && !loadErrorMessage}
                    shouldShowLogo={false}
                    shouldShowLinks
                >
                    {loadErrorMessage && (
                        <MessageBox variant="error">
                            {loadErrorMessage}
                        </MessageBox>
                    )}
                    {isTargetReady && (
                        <MfaSettings
                            mode="required"
                            onContinue={handleContinue}
                            onCancel={handleCancel}
                        />
                    )}
                </AuthPage>
            </ThemeProvider>
        </StyledEngineProvider>
    );
}

export default MfaRequired;
